'use client';
import React from 'react';

import { ColumnDef as ColumnDefTS, Column, getCoreRowModel, useReactTable, CellContext } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';

import styled from '@benzinga/themetron';
import Hooks, { useNearEndScroll, useScrollShadow } from '@benzinga/hooks';
import { ErrorBoundary, Tooltip } from '@benzinga/core-ui';

import { StyledTableRow, StyledTableDataCell } from '../Table/TableRow';
import { SortBy, getCellRendererRowValue, sortRowData } from '../../libs/utils';
import { useScrollbarWidth } from '../../libs/useScrollbarWidth';
import { ColumnDef } from '../../interface';
import { StyledShadow, StyledTBody, StyledTable } from '../Table';
import { TableContainer } from '../shared';
import { BaseTableProps } from '../shared/types';
import { TableHead } from './TableHead';

export type CombinedColumnDefTS<T> = ColumnDefTS<T> & ColumnDef<T>;

export interface TableVirtualizedProps<T = unknown> extends BaseTableProps<T> {
  customNoDataElement?: React.ReactElement;
}

interface CellProps {
  ariaHidden?: boolean;
  className?: string;
  colSpan?: number;
  title?: string;
  tooltip?: string | null;
  tooltipWidth?: number;
  width?: number;
}

export const TableVirtualized = <T,>({
  adPlacement,
  className,
  colorRowByValue = false,
  colorRowField = '',
  columnsDef,
  customNoDataElement,
  getRowId,
  hasStockTableStyling,
  height = 500,
  hiddenColumns,
  hiddenColumnsWhenGated,
  isGated = false,
  isLoading = false,
  loadingText,
  mobileHeight,
  nearEndOffsetPx,
  noDataText,
  onNearEnd,
  rowData,
  seeMoreLink,
  seeMoreText,
  title,
}: TableVirtualizedProps<T>) => {
  const [sortBy, setSortBy] = React.useState<SortBy>({});

  const ref = React.useRef<HTMLDivElement | null>(null);
  const tableWrapperRef = React.useRef<HTMLDivElement>(null);

  const { width } = Hooks.useRefElementSize(ref);
  const isGreaterThan700 = width > 700;

  const scrollbarWidth = useScrollbarWidth();
  const { showEnd: showScrollShadow } = useScrollShadow(tableWrapperRef);
  useNearEndScroll(tableWrapperRef, {
    enabled: !!onNearEnd && !isLoading,
    onNearEnd,
    thresholdPx: nearEndOffsetPx,
  });

  const filteredColumnsDef = (columnsDef ?? []).filter(column => {
    const shouldBeHidden = Array.isArray(hiddenColumns) && hiddenColumns.includes(column.field);
    return !shouldBeHidden;
  });

  const sortedRowData = React.useMemo(() => sortRowData(rowData ?? [], sortBy), [rowData, sortBy]);

  const tanStackColumns = React.useMemo<CombinedColumnDefTS<T>[]>(() => {
    if (!Array.isArray(filteredColumnsDef)) return [];

    return filteredColumnsDef.map(column => {
      let accessorFn: ((row: any, index: number) => string | number | React.ReactElement) | undefined;

      if (column.cellRenderer || column.valueGetter || column.valueFormatter) {
        accessorFn = (row, index) => getCellRendererRowValue(column, index, row);
      } else {
        accessorFn = undefined;
      }

      const result: CombinedColumnDefTS<T> = {
        ...column,
        accessorFn,
        accessorKey: column.field,
        header: column.headerName || column.field,
        size: column.width,
      };

      return result;
    });
  }, [filteredColumnsDef]);

  const table = useReactTable({
    columns: tanStackColumns,
    data: sortedRowData,
    getCoreRowModel: getCoreRowModel(),
    getRowId: getRowId ? (originalRow, index) => getRowId(originalRow, index) : undefined,
  });

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 32,
    getScrollElement: () => tableWrapperRef.current,
    overscan: height ? Math.round(height / 32.5) : 15,
  });
  const { getTotalSize, getVirtualItems } = rowVirtualizer;
  const virtualRows = getVirtualItems();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom = virtualRows.length > 0 ? getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  const getCellProps = (context: CellContext<T, unknown>): CellProps => {
    const columnDef = context.column.columnDef as unknown as ColumnDef;
    const data = context.row.original;
    const index = context.row.index;

    const rawValue: string | number | null =
      (typeof columnDef.valueGetter === 'function' && columnDef.valueGetter({ data })) ||
      (data as Record<string, string | number>)[columnDef.field] ||
      null;

    const cellClassname =
      typeof columnDef.cellClass === 'function'
        ? columnDef.cellClass({ data, index, value: rawValue })
        : columnDef.cellClass
          ? columnDef.cellClass
          : '';

    const baseClassName = classNames(`table-cell-${columnDef.field} max-w-[300px] sm:max-w-[unset] truncate`, {
      [`${cellClassname}`]: !!cellClassname,
    });

    const isRowGated = isGated && index > 2;
    const className = isRowGated
      ? `blur-sm pointer-events-none select-none ${baseClassName}`
      : `${baseClassName} relative group overflow-visible`;

    const tooltip: string | null =
      (typeof columnDef.tooltipValueGetter === 'function' && columnDef.tooltipValueGetter({ data })) || null;

    const fieldValue = (data as Record<string, unknown>)[columnDef.field];
    const title = columnDef.shouldRender
      ? undefined
      : typeof fieldValue === 'string' || typeof fieldValue === 'number'
        ? String(fieldValue)
        : typeof rawValue === 'string' || typeof rawValue === 'number'
          ? String(rawValue)
          : undefined;

    return {
      ariaHidden: columnDef.ariaHidden,
      className,
      colSpan: columnDef.colSpan ?? 1,
      title,
      tooltip,
      tooltipWidth: columnDef.tooltipWidth,
      width: columnDef.width,
    };
  };

  const handleSortChange = React.useCallback(
    (newSortByField: string, column: Column<T>) => {
      let direction: 'desc' | 'asc' = 'asc';
      const columnDef = column.columnDef as CombinedColumnDefTS<T>;
      React.startTransition(() => {
        if (sortBy.field !== newSortByField) {
          direction = 'asc';
          setSortBy({ columnDef, direction, field: newSortByField });
        } else if (sortBy?.direction === 'asc') {
          direction = 'desc';
          setSortBy({ columnDef, direction, field: newSortByField });
        } else if (sortBy?.direction === 'desc') {
          setSortBy({});
        } else {
          setSortBy({ columnDef, direction, field: newSortByField });
        }
      });
    },
    [sortBy],
  );

  const isLastColumnSortable = !filteredColumnsDef[filteredColumnsDef.length - 1]?.sortEnabled || false;

  return (
    <TableContainer
      alignLastRowsRight={isLastColumnSortable}
      className={classNames('benzinga-core-virtualized-table-container', { [`${className}`]: !!className })}
      containerRef={ref}
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      isLoading={isLoading}
      loadingText={loadingText}
      mobileHeight={mobileHeight}
      seeMoreLink={seeMoreLink}
      seeMoreText={seeMoreText}
      title={title}
    >
      <VirtualizedTableWrapper
        $hasStockTableStyling={hasStockTableStyling}
        $height={height}
        $mobileHeight={mobileHeight}
        className="benzinga-core-virtualized-table-wrapper overflow-x-auto no-scrollbar relative"
        ref={tableWrapperRef}
      >
        {sortedRowData.length === 0 && (
          <div className="flex items-center justify-center absolute w-full h-full text-sm">
            {customNoDataElement || (
              <span className="font-semibold">{noDataText || 'No data available to display'}</span>
            )}
          </div>
        )}
        <StyledTable
          $alignLastRowsRight={isLastColumnSortable}
          $hasStockTableStyling={hasStockTableStyling}
          className={classNames('benzinga-core-virtualized-table min-w-[600px] relative', {
            'min-w-[unset]': isGreaterThan700,
          })}
        >
          <TableHead
            $alignLastRowsRight={isLastColumnSortable}
            $hasStockTableStyling={hasStockTableStyling}
            hiddenColumnsWhenGated={hiddenColumnsWhenGated}
            isGated={isGated}
            onSortChange={handleSortChange}
            sortBy={sortBy}
            table={table}
          />
          <StyledTBody className="benzinga-core-virtualized-table-tbody whitespace-nowrap">
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {virtualRows.map(virtualRow => {
              const row = rows[virtualRow.index];
              const rowData = row.original as Record<string, unknown>;
              const rowClassName =
                colorRowByValue && rowData[colorRowField]
                  ? (rowData[colorRowField] as number) > 0
                    ? 'row-green'
                    : (rowData[colorRowField] as number) < 0
                      ? 'row-red'
                      : ''
                  : '';

              return (
                <React.Fragment key={`benzinga-core-virtualized-table-row-${row.id}`}>
                  <StyledTableRow
                    $hasStockTableStyling={hasStockTableStyling}
                    className={classNames(rowClassName, {
                      'blur-md': isGated && virtualRow.index > 2,
                    })}
                  >
                    {row.getVisibleCells().map(cell => {
                      const cellProps = getCellProps(cell.getContext());
                      const cellContent = cell.renderValue() as React.ReactNode;

                      return (
                        <StyledTableDataCell
                          $width={cellProps.width}
                          aria-hidden={cellProps.ariaHidden}
                          className={cellProps.className}
                          colSpan={cellProps.colSpan}
                          key={cell.id}
                          title={cellProps.title}
                        >
                          {cellProps.tooltip ? (
                            <Tooltip content={cellProps.tooltip} isContentHtml width={cellProps.tooltipWidth ?? 100}>
                              {cellContent || '—'}
                            </Tooltip>
                          ) : (
                            cellContent || '—'
                          )}
                        </StyledTableDataCell>
                      );
                    })}
                  </StyledTableRow>
                  {adPlacement && sortedRowData.length > 3 && virtualRow.index === 2 && (
                    <ErrorBoundary name="stock-movers-table-ad-placement">
                      <StyledTableRow $hasStockTableStyling={hasStockTableStyling}>
                        <td colSpan={filteredColumnsDef.length}>
                          <div>{adPlacement}</div>
                        </td>
                      </StyledTableRow>
                    </ErrorBoundary>
                  )}
                </React.Fragment>
              );
            })}
            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </StyledTBody>
        </StyledTable>
      </VirtualizedTableWrapper>
      <StyledShadow $right={scrollbarWidth} $visible={showScrollShadow} />
    </TableContainer>
  );
};

export default TableVirtualized;

const VirtualizedTableWrapper = styled.div<{
  $hasStockTableStyling?: boolean;
  $height?: number;
  $mobileHeight?: number;
}>`
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  border-left: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '1px solid #aaa' : '1px solid #f0f0f0')};
  border-right: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '1px solid #aaa' : '1px solid #f0f0f0')};
  border-bottom: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '1px solid #aaa' : '1px solid #f0f0f0')};

  @media (max-width: 640px) {
    height: ${({ $height, $mobileHeight }) =>
      $mobileHeight ? `${$mobileHeight}px` : $height ? `${$height}px` : 'auto'};
  }

  table {
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;

    th {
      padding: 0.4rem;
    }
  }

  thead {
    margin: 0;
    position: sticky;
    top: 0;
    z-index: 2;
  }
`;
